import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BriefcaseBusiness,
  Building2,
  Eye,
  Pencil,
  RefreshCw,
  UserPlus,
  Users,
  UserX,
  WalletCards,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useEmployees } from '../../hooks/useHR';
import { employeesService } from '../../services/hrService';
import type { Employee } from '../../types/firestore';
import type { DataTableColumn } from '../../types/ui';
import CreateEmployeeModal from '../../components/hr/CreateEmployeeModal';
import EditEmployeeModal from '../../components/hr/EditEmployeeModal';
import PendingEmployeesModal from '../../components/hr/PendingEmployeesModal';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import EmptyState from '../../components/ui/EmptyState';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import MetricCard from '../../components/ui/MetricCard';
import Modal from '../../components/ui/Modal';
import PageHeader from '../../components/ui/PageHeader';
import StatusBadge from '../../components/ui/StatusBadge';
import Surface from '../../components/ui/Surface';

type StatusFilter = 'all' | 'active' | 'inactive';

const accountStatusLabels: Record<string, string> = {
  invited: 'مدعو',
  active: 'نشط',
  suspended: 'موقوف',
  terminated: 'منتهي',
};

const formatTimestamp = (value: unknown) => {
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate().toLocaleDateString('ar-EG');
  }
  return 'غير محدد';
};

const EmployeesManagement: React.FC = () => {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const {
    employees,
    loading,
    error,
    fetchEmployees,
    deactivateEmployee,
  } = useEmployees();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [canCreateEmployees, setCanCreateEmployees] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [pendingDeactivation, setPendingDeactivation] =
    useState<Employee | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      setCanCreateEmployees(false);
      return () => {
        active = false;
      };
    }
    employeesService
      .canCreateEmployees(user.id)
      .then(result => {
        if (active) setCanCreateEmployees(result);
      })
      .catch(() => {
        if (active) setCanCreateEmployees(false);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);

  const departments = useMemo(
    () =>
      Array.from(
        new Set(employees.map(employee => employee.department).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, 'ar')),
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('ar');
    return employees.filter(employee => {
      const matchesSearch =
        !term ||
        [employee.name, employee.email, employee.employeeId, employee.position]
          .filter(Boolean)
          .some(value => value.toLocaleLowerCase('ar').includes(term));
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && employee.isActive) ||
        (statusFilter === 'inactive' && !employee.isActive);
      const matchesDepartment =
        departmentFilter === 'all' || employee.department === departmentFilter;
      return matchesSearch && matchesStatus && matchesDepartment;
    });
  }, [departmentFilter, employees, search, statusFilter]);

  const stats = useMemo(
    () => ({
      total: employees.length,
      active: employees.filter(employee => employee.isActive).length,
      departments: departments.length,
      payroll: employees
        .filter(employee => employee.isActive)
        .reduce((sum, employee) => sum + (employee.salary?.base || 0), 0),
    }),
    [departments.length, employees]
  );

  const columns = useMemo<DataTableColumn<Employee>[]>(
    () => [
      {
        key: 'employee',
        header: 'الموظف',
        cell: employee => (
          <div className='min-w-0'>
            <p className='truncate font-semibold text-secondary-950 dark:text-white'>
              {employee.name}
            </p>
            <p className='truncate text-xs text-secondary-500'>
              {employee.email} · {employee.employeeId}
            </p>
          </div>
        ),
      },
      {
        key: 'job',
        header: 'الوظيفة',
        cell: employee => (
          <div>
            <p className='font-medium text-secondary-800 dark:text-secondary-100'>
              {employee.position || 'غير محدد'}
            </p>
            <p className='text-xs text-secondary-500'>{employee.department}</p>
          </div>
        ),
      },
      {
        key: 'salary',
        header: 'الراتب',
        hideOnMobile: true,
        cell: employee => formatAmount(employee.salary?.base || 0),
      },
      {
        key: 'status',
        header: 'الحالة',
        cell: employee => (
          <StatusBadge tone={employee.isActive ? 'success' : 'neutral'}>
            {accountStatusLabels[employee.accountStatus || ''] ||
              (employee.isActive ? 'نشط' : 'غير نشط')}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: 'الإجراءات',
        className: 'text-left',
        cell: employee => (
          <div className='flex flex-wrap justify-end gap-1'>
            <Button
              size='sm'
              variant='ghost'
              aria-label={`عرض تفاصيل ${employee.name}`}
              onClick={() => {
                setSelectedEmployee(employee);
                setShowEditModal(false);
              }}
            >
              <Eye className='h-4 w-4' />
            </Button>
            <Button
              size='sm'
              variant='ghost'
              aria-label={`تعديل ${employee.name}`}
              onClick={() => {
                setSelectedEmployee(employee);
                setShowEditModal(true);
              }}
            >
              <Pencil className='h-4 w-4' />
            </Button>
            {employee.isActive ? (
              <Button
                size='sm'
                variant='ghost'
                className='text-danger-600 hover:text-danger-700'
                aria-label={`إلغاء تفعيل ${employee.name}`}
                onClick={() => setPendingDeactivation(employee)}
              >
                <UserX className='h-4 w-4' />
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [formatAmount]
  );

  const confirmDeactivation = async () => {
    if (!pendingDeactivation) return;
    setDeactivating(true);
    try {
      await deactivateEmployee(pendingDeactivation.id);
      toast.success('تم إلغاء تفعيل الموظف');
      setPendingDeactivation(null);
      if (selectedEmployee?.id === pendingDeactivation.id) {
        setSelectedEmployee(null);
      }
    } catch {
      toast.error('تعذر إلغاء تفعيل الموظف');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <Surface className='flex min-h-[24rem] items-center justify-center'>
        <LoadingSpinner size='large' />
      </Surface>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        eyebrow='الموارد البشرية'
        title='إدارة الموظفين'
        description='إدارة الحسابات الوظيفية والرواتب والحالة التشغيلية من بيانات النظام الفعلية.'
        actions={
          <>
            <Button
              variant='secondary'
              onClick={() => setShowPendingModal(true)}
              disabled={!canCreateEmployees}
              leadingIcon={<Users className='h-4 w-4' />}
            >
              الموظفون المعلقون
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              disabled={!canCreateEmployees}
              leadingIcon={<UserPlus className='h-4 w-4' />}
            >
              إضافة موظف
            </Button>
          </>
        }
      />

      {error ? (
        <Surface>
          <EmptyState
            icon={AlertTriangle}
            title='تعذر تحميل الموظفين'
            description={error}
            action={
              <Button
                variant='secondary'
                onClick={fetchEmployees}
                leadingIcon={<RefreshCw className='h-4 w-4' />}
              >
                إعادة المحاولة
              </Button>
            }
          />
        </Surface>
      ) : null}

      <div className='grid grid-cols-2 gap-3 xl:grid-cols-4'>
        <MetricCard label='إجمالي الموظفين' value={stats.total} icon={Users} />
        <MetricCard
          label='الموظفون النشطون'
          value={stats.active}
          icon={BriefcaseBusiness}
          tone='green'
        />
        <MetricCard
          label='الأقسام المسجلة'
          value={stats.departments}
          icon={Building2}
          tone='amber'
        />
        <MetricCard
          label='إجمالي الرواتب الأساسية'
          value={formatAmount(stats.payroll)}
          icon={WalletCards}
          tone='red'
        />
      </div>

      <Surface>
        <div className='grid gap-4 md:grid-cols-3'>
          <Input
            label='البحث'
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder='الاسم أو البريد أو رقم الموظف'
          />
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
            <span>الحالة</span>
            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value as StatusFilter)}
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              <option value='all'>كل الحالات</option>
              <option value='active'>نشط</option>
              <option value='inactive'>غير نشط</option>
            </select>
          </label>
          <label className='space-y-2 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
            <span>القسم</span>
            <select
              value={departmentFilter}
              onChange={event => setDepartmentFilter(event.target.value)}
              className='min-h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-700 dark:bg-secondary-800 dark:text-white'
            >
              <option value='all'>كل الأقسام</option>
              {departments.map(department => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Surface>

      <Surface padding='none'>
        <DataTable
          caption='قائمة الموظفين'
          columns={columns}
          rows={filteredEmployees}
          rowKey={employee => employee.id}
          emptyState={
            <EmptyState
              icon={Users}
              title='لا توجد نتائج'
              description={
                employees.length
                  ? 'لا يوجد موظفون يطابقون الفلاتر الحالية.'
                  : 'لم تتم إضافة موظفين بعد.'
              }
            />
          }
        />
      </Surface>

      <CreateEmployeeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          void fetchEmployees();
        }}
      />
      <PendingEmployeesModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        ownerId={user?.id}
        onEmployeeActivated={() => {
          setShowPendingModal(false);
          void fetchEmployees();
        }}
      />
      {selectedEmployee && showEditModal ? (
        <EditEmployeeModal
          employee={selectedEmployee}
          isOpen
          onClose={() => {
            setShowEditModal(false);
            setSelectedEmployee(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedEmployee(null);
            void fetchEmployees();
          }}
        />
      ) : null}

      <Modal
        isOpen={Boolean(selectedEmployee && !showEditModal)}
        onClose={() => setSelectedEmployee(null)}
        title='تفاصيل الموظف'
        description={selectedEmployee?.employeeId}
        size='lg'
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => setSelectedEmployee(null)}
            >
              إغلاق
            </Button>
            {selectedEmployee ? (
              <Button onClick={() => setShowEditModal(true)}>تعديل البيانات</Button>
            ) : null}
          </>
        }
      >
        {selectedEmployee ? (
          <div className='grid gap-4 sm:grid-cols-2'>
            <Detail label='الاسم' value={selectedEmployee.name} />
            <Detail label='البريد' value={selectedEmployee.email} />
            <Detail label='الهاتف' value={selectedEmployee.phone || 'غير محدد'} />
            <Detail label='المنصب' value={selectedEmployee.position || 'غير محدد'} />
            <Detail label='القسم' value={selectedEmployee.department} />
            <Detail
              label='الراتب الأساسي'
              value={formatAmount(selectedEmployee.salary?.base || 0)}
            />
            <Detail
              label='تاريخ التعيين'
              value={formatTimestamp(selectedEmployee.employment?.hireDate)}
            />
            <Detail
              label='الحالة'
              value={selectedEmployee.isActive ? 'نشط' : 'غير نشط'}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={Boolean(pendingDeactivation)}
        onClose={() => {
          if (!deactivating) setPendingDeactivation(null);
        }}
        title='إلغاء تفعيل الموظف'
        description='سيُمنع الموظف من استخدام صلاحياته التشغيلية.'
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => setPendingDeactivation(null)}
              disabled={deactivating}
            >
              إلغاء
            </Button>
            <Button
              variant='danger'
              onClick={confirmDeactivation}
              loading={deactivating}
            >
              تأكيد إلغاء التفعيل
            </Button>
          </>
        }
      >
        <p className='text-sm text-secondary-600 dark:text-secondary-300'>
          الموظف: {pendingDeactivation?.name}
        </p>
      </Modal>
    </div>
  );
};

const Detail: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className='rounded-xl bg-secondary-50 p-4 dark:bg-secondary-800'>
    <p className='text-xs font-semibold text-secondary-500'>{label}</p>
    <div className='mt-1 font-medium text-secondary-950 dark:text-white'>{value}</div>
  </div>
);

export default EmployeesManagement;
